/**
 * Better Auth Client - Frontend Authentication Integration
 *
 * Provides Better Auth client setup for frontend authentication operations.
 * Integrates with our port-first architecture and ENV_KEYS system.
 *
 * @example
 * ```typescript
 * import { signIn, signUp, useSession } from '@/lib/auth-client';
 *
 * // Sign in
 * const result = await signIn.email({
 *   email: 'user@example.com',
 *   password: 'password'
 * });
 *
 * // Use session in React component
 * const session = useSession();
 * ```
 */

import { createAuthClient } from "better-auth/client";
import { adminClient, deviceAuthorizationClient, magicLinkClient } from "better-auth/client/plugins";
import { useStore } from "@nanostores/react";

// Get API base URL from Vite env injection
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
if (!API_BASE_URL) {
  throw new Error('VITE_API_BASE_URL is not configured');
}

const API_ADMIN_BASE_URL = import.meta.env.VITE_API_ADMIN_BASE_URL;
if (!API_ADMIN_BASE_URL) {
  throw new Error('VITE_API_ADMIN_BASE_URL is not configured');
}

/**
 * Better Auth Client Configuration
 *
 * Main client for user authentication operations.
 * Automatically handles cookies, sessions, and CSRF protection.
 */
/** Absolute base of api-app (port 3002 in development), resolved from a relative VITE value. */
const RESOLVED_API_BASE_URL =
  API_BASE_URL.startsWith('/') && typeof window !== 'undefined'
    ? new URL(API_BASE_URL, window.location.origin).href
    : API_BASE_URL;

export const authClient = createAuthClient({
  baseURL: RESOLVED_API_BASE_URL, // Points to api-app on port 3002
  fetchOptions: {
    credentials: 'include' as const,
  },
  plugins: [
    adminClient(), // Add admin capabilities when needed
    magicLinkClient(),
    deviceAuthorizationClient() // Approve / deny desktop-app sign-ins at /device
  ]
});

/**
 * User Authentication Operations
 *
 * Direct exports from Better Auth client for easy consumption.
 * These methods automatically handle cookies and session management.
 */
export const {
  signIn,
  signUp,
  signOut,
  getSession,
  updateUser,
  changePassword,
  requestPasswordReset,
  resetPassword,
  verifyEmail,
  $fetch // Better Fetch instance for custom API calls
  // No `twoFactor`: the server plugin is not registered (there is no twoFactor table), so
  // every two-factor call would 404. See better-auth-config.ts for what to restore first.
} = authClient;

/**
 * What happened when a verification link was followed.
 */
export type EmailVerificationOutcome =
  | { status: 'verified' }
  | { status: 'already-verified' }
  | { status: 'expired' }
  | { status: 'invalid' }
  | { status: 'failed'; message: string };

/**
 * Verify an email address with the token from the emailed link.
 *
 * Better Auth 1.5.6 serves this as `GET /api/auth/verify-email?token=…&callbackURL=…`; there is no
 * POST. We deliberately leave `callbackURL` off: with one, the endpoint answers every outcome as a
 * redirect (to the URL, or to `?error=CODE`) and the page would never see what happened. Without
 * one it answers JSON on success and a 401 carrying `{ code, message }` on failure, and it still
 * sets the session cookie, so `autoSignInAfterVerification` works exactly the same.
 *
 * A subtlety worth knowing: on success the endpoint returns `{ status: true, user: null }` whether
 * it just verified the address or found it already verified. The two are told apart by the session
 * we held *before* the call — the only signal available.
 */
export const verifyEmailToken = async (token: string): Promise<EmailVerificationOutcome> => {
  let wasAlreadyVerified = false;
  try {
    const existing = await getSession();
    wasAlreadyVerified = existing?.data?.user?.emailVerified === true;
  } catch {
    // No readable session: treat the outcome below as a fresh verification
  }

  const url = new URL(`${RESOLVED_API_BASE_URL}/api/auth/verify-email`);
  url.searchParams.set('token', token);

  const { data, error } = await $fetch<{ status?: boolean }>(url.toString(), {
    method: 'GET',
    credentials: 'include',
  }) as {
    data: { status?: boolean } | null;
    error: { code?: string; message?: string; status?: number } | null;
  };

  if (error) {
    // A server answer carries a Better Auth error code; a transport failure carries none.
    switch (error.code) {
      case 'TOKEN_EXPIRED':
        return { status: 'expired' };
      case 'INVALID_TOKEN':
      case 'USER_NOT_FOUND':
        return { status: 'invalid' };
      default:
        return { status: 'failed', message: error.message || 'We could not reach the server.' };
    }
  }

  if (data?.status !== true) {
    return { status: 'failed', message: 'The server gave an unexpected answer.' };
  }

  return { status: wasAlreadyVerified ? 'already-verified' : 'verified' };
};

/**
 * Ask for a fresh verification link — the way out of an expired or invalid one.
 *
 * `POST /api/auth/send-verification-email` deliberately answers `{ status: true }` for an unknown
 * or already-verified address when nobody is signed in, so this never reveals who has an account.
 * `callbackURL` is where the new link lands the user once Better Auth has verified and signed
 * them in; it matches what sign-up sends.
 */
export const resendVerificationEmail = async (
  email: string,
  callbackURL: string,
): Promise<{ ok: boolean; message?: string }> => {
  const { data, error } = await $fetch<{ status?: boolean }>(
    `${RESOLVED_API_BASE_URL}/api/auth/send-verification-email`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, callbackURL }),
    },
  ) as {
    data: { status?: boolean } | null;
    error: { code?: string; message?: string } | null;
  };

  if (error) {
    if (error.code === 'EMAIL_ALREADY_VERIFIED') {
      return { ok: false, message: 'That address is already verified — try signing in.' };
    }
    return { ok: false, message: error.message || 'We could not send a new link.' };
  }

  if (data?.status !== true) {
    return { ok: false, message: 'We could not send a new link.' };
  }

  return { ok: true };
};

// Create useSession hook using nanostores React adapter - SSR safe
export function useSession() {
  // Always call the hook (Rules of Hooks requirement)
  const session = useStore(authClient.useSession);

  // Return null session for SSR
  if (typeof window === 'undefined') {
    return {
      data: null,
      error: null,
      isPending: false
    };
  }

  return session;
}

/**
 * Admin Authentication Client (Optional)
 *
 * Better Auth uses the same client for both user and admin operations.
 * Admin functionality is handled through the adminClient plugin.
 */
export const adminAuthClient = authClient; // Same client, admin capabilities via plugin

/**
 * Session Management Utilities
 *
 * Helper functions for common session operations.
 */
export const sessionUtils = {
  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const session = await getSession();
      return !!session.data?.user;
    } catch {
      return false;
    }
  },

  /**
   * Get current user safely
   */
  async getCurrentUser() {
    try {
      const session = await getSession();
      return session.data?.user || null;
    } catch {
      return null;
    }
  },

  /**
   * Check if current user has admin role
   */
  async isAdmin(): Promise<boolean> {
    try {
      const session = await getSession();
      return session.data?.user?.role === 'admin';
    } catch {
      return false;
    }
  }
};

/**
 * Authentication Error Types
 *
 * Better Auth error handling with type safety.
 */
export type AuthError = {
  message: string;
  status: number;
  statusText: string;
};

/**
 * Session Data Types
 *
 * TypeScript types for session data structure.
 */
export type SessionData = {
  user: {
    id: string;
    email: string;
    name: string;
    role?: string;
    emailVerified: boolean;
    image?: string;
    createdAt: Date;
    updatedAt: Date;
  } | null;
  session: {
    id: string;
    userId: string;
    expiresAt: Date;
    token: string;
    ipAddress?: string;
    userAgent?: string;
  } | null;
};

/**
 * Authentication Operations Type
 *
 * Type-safe interface for all auth operations.
 */
export type AuthOperations = typeof authClient;

// Re-export Better Auth types for convenience
export type { User, Session } from "better-auth/types";
